import { formatOperationalNumber } from "./number-format.js";

export const MONITORING_CARD_WIDTH = 1080;
export const MONITORING_CARD_HEIGHT = 1350;
export const MONITORING_MAX_STOPS_PER_PAGE = 7;

const MONITORING_STOP_GAP = 8;
const MONITORING_STOP_AREA_HEIGHT = 744;
const MONITORING_TITLE_CHAR_LIMIT = 58;
const MONITORING_ORDER_CHAR_LIMIT = 72;

const BRAND = {
  primary: "#2F6F73",
  dark: "#24575B",
  soft: "#E7F1F1",
  warm: "#D97941",
  ink: "#17333A",
  muted: "#667085",
  line: "#D9E3E4",
  canvas: "#F6FAFA",
  danger: "#B42318",
  success: "#166534",
  scheduled: "#1D4ED8",
};

const dateFormatter = new Intl.DateTimeFormat("id-ID", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const timestampFormatter = new Intl.DateTimeFormat("id-ID", {
  dateStyle: "medium",
  timeStyle: "short",
});

function text(value, fallback = "—") {
  const normalized = String(value ?? "").trim();

  return normalized || fallback;
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;

  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : null;
}

function formatDate(value, fallback = "—") {
  if (!value) return fallback;

  const parsed =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);

  if (Number.isNaN(parsed.getTime())) return fallback;

  return dateFormatter.format(parsed);
}

function formatTimestamp(value = new Date()) {
  const parsed = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsed.getTime())) return timestampFormatter.format(new Date());

  return timestampFormatter.format(parsed);
}

function formatMetric(value, unit) {
  const numeric = toFiniteNumber(value);

  if (numeric === null) return "—";

  return `${formatOperationalNumber(numeric, "—")} ${unit}`;
}

function sanitizeFilename(value) {
  return text(value, "fleet-order")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getMonitoringDocument(order) {
  const documentType = String(order?.documentType || order?.sourceType || "SALES_ORDER")
    .trim()
    .toUpperCase();
  const documentNumber = text(
    order?.documentNumber ||
      order?.invoiceNumber ||
      order?.reverseInvoiceNumber ||
      order?.soNumber,
    "",
  );
  const labelPrefix =
    documentType === "REVERSE_INVOICE" || order?.invoiceNumber
      ? "Faktur"
      : "SO";

  return {
    ...order,
    documentType,
    documentNumber,
    soNumber: text(order?.soNumber || documentNumber, ""),
    orderDate: order?.orderDate || order?.documentDate || order?.transactionDate || null,
    displayLabel: documentNumber ? `${labelPrefix}: ${documentNumber}` : "",
  };
}

function uniqueOrders(orders) {
  const seen = new Set();

  return (Array.isArray(orders) ? orders : [])
    .map(getMonitoringDocument)
    .filter((order) => {
      if (!order.documentNumber) return false;

      const key = `${order.documentType}:${order.documentNumber}`.toLowerCase();

      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
}


function splitTextByCharacterBudget(value, maxCharacters, maxLines = Infinity) {
  const words = text(value, "").split(/\s+/).filter(Boolean);

  if (!words.length) return [];

  const lines = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;

    if (!current || candidate.length <= maxCharacters) {
      current = candidate;
      continue;
    }

    lines.push(current);
    current = word;
  }

  if (current) lines.push(current);

  if (lines.length <= maxLines) return lines;

  const visible = lines.slice(0, maxLines);
  const remaining = lines.slice(maxLines - 1).join(" ");
  const suffix = "…";
  const available = Math.max(1, maxCharacters - suffix.length);

  visible[maxLines - 1] = `${remaining.slice(0, available).trimEnd()}${suffix}`;

  return visible;
}

function buildOrderLines(stop) {
  const labels = stop.orders.length
    ? stop.orders.map((order) => order.displayLabel || order.documentNumber)
    : ["Dokumen belum tersedia"];
  const lines = [];
  let current = "";

  for (const label of labels) {
    const candidate = current ? `${current} · ${label}` : label;

    if (!current || candidate.length <= MONITORING_ORDER_CHAR_LIMIT) {
      current = candidate;
      continue;
    }

    lines.push(current);
    current = label;
  }

  if (current) lines.push(current);

  return lines.length ? lines : ["Dokumen belum tersedia"];
}

function getMonitoringStopLayout(stop) {
  const titleLines = splitTextByCharacterBudget(
    stop.customerName,
    MONITORING_TITLE_CHAR_LIMIT,
    2,
  );
  const orderLines = buildOrderLines(stop);

  /*
   * Baseline layout:
   * - one customer title line;
   * - one destination metadata line;
   * - one or more complete document lines.
   *
   * The number of document lines increases the card height. Document numbers are never
   * replaced with "+N lainnya" and are never intentionally clipped.
   */
  const height =
    94 +
    Math.max(0, titleLines.length - 1) * 25 +
    Math.max(0, orderLines.length - 1) * 20;

  return {
    height,
    titleLines: titleLines.length ? titleLines : ["Pelanggan belum tersedia"],
    orderLines,
  };
}

function getMonitoringPageHeight(stops) {
  if (!stops.length) return 0;

  return (
    stops.reduce(
      (total, stop) => total + Number(stop.monitoringLayout?.height || 94),
      0,
    ) +
    Math.max(0, stops.length - 1) * MONITORING_STOP_GAP
  );
}

function getMinimumMonitoringPageCount(stops, maxStopsPerPage) {
  if (!stops.length) return 0;

  let pageCount = 1;
  let currentStops = [];
  let currentHeight = 0;

  stops.forEach((stop) => {
    const stopHeight = Number(stop.monitoringLayout?.height || 94);
    const nextHeight =
      currentHeight +
      (currentStops.length ? MONITORING_STOP_GAP : 0) +
      stopHeight;

    if (
      currentStops.length > 0 &&
      (currentStops.length >= maxStopsPerPage ||
        nextHeight > MONITORING_STOP_AREA_HEIGHT)
    ) {
      pageCount += 1;
      currentStops = [stop];
      currentHeight = stopHeight;
      return;
    }

    currentStops.push(stop);
    currentHeight = nextHeight;
  });

  return pageCount;
}

function partitionMonitoringStops(stops, pageCount, maxStopsPerPage) {
  const pages = [];
  let cursor = 0;

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const remainingPages = pageCount - pageIndex;
    const remainingStops = stops.length - cursor;

    if (remainingPages === 1) {
      pages.push(stops.slice(cursor));
      break;
    }

    const remaining = stops.slice(cursor);
    const targetHeight = getMonitoringPageHeight(remaining) / remainingPages;
    const minimumStopsToLeave = remainingPages - 1;
    const maximumCandidateLength = Math.min(
      maxStopsPerPage,
      remainingStops - minimumStopsToLeave,
    );

    let selectedLength = 1;
    let selectedDifference = Infinity;

    for (let length = 1; length <= maximumCandidateLength; length += 1) {
      const candidate = remaining.slice(0, length);
      const candidateHeight = getMonitoringPageHeight(candidate);
      const stopsLeft = remainingStops - length;

      if (candidateHeight > MONITORING_STOP_AREA_HEIGHT && length > 1) {
        break;
      }

      if (stopsLeft > (remainingPages - 1) * maxStopsPerPage) {
        continue;
      }

      const difference = Math.abs(targetHeight - candidateHeight);

      if (difference < selectedDifference) {
        selectedDifference = difference;
        selectedLength = length;
      }
    }

    pages.push(stops.slice(cursor, cursor + selectedLength));
    cursor += selectedLength;
  }

  return pages;
}

export function formatMonitoringStatus(status) {
  const normalized = String(status || "SCHEDULED").toUpperCase();

  if (normalized === "CANCELLED") {
    return {
      code: normalized,
      label: "Dibatalkan",
      badge: "DIBATALKAN",
      captionTitle: "MONITORING PENGIRIMAN — DIBATALKAN",
      color: BRAND.danger,
      background: "#FEE4E2",
    };
  }

  if (normalized === "COMPLETED") {
    return {
      code: normalized,
      label: "Selesai",
      badge: "SELESAI",
      captionTitle: "MONITORING PENGIRIMAN",
      color: BRAND.success,
      background: "#DCFCE7",
    };
  }

  return {
    code: normalized,
    label: "Terjadwal",
    badge: "TERJADWAL",
    captionTitle: "MONITORING PENGIRIMAN",
    color: BRAND.scheduled,
    background: "#DBEAFE",
  };
}

export function normalizeMonitoringStops(fleetOrder) {
  return (Array.isArray(fleetOrder?.stops) ? fleetOrder.stops : []).map((stop, index) => {
    const orders = uniqueOrders(stop?.orders);
    const addressAlias = text(
      stop?.addressAlias || stop?.shippingAddressAlias || stop?.customerShippingAddressAlias,
      "",
    );
    const routeName =
      stop?.resolvedRouteName ||
      stop?.resolvedRouteCode ||
      stop?.resolvedRoute ||
      stop?.routeName ||
      stop?.routeCode;

    return {
      ...stop,
      displaySequence: Number(stop?.displaySequence || stop?.sequence || index + 1),
      customerName: text(stop?.customerName, "Pelanggan belum tersedia"),
      addressAlias: addressAlias || "Alamat default",
      hasCustomAddressAlias: Boolean(addressAlias && addressAlias !== "Alamat default"),
      resolvedArea: text(stop?.resolvedArea || stop?.area, "Area belum tersedia"),
      resolvedRouteName: text(routeName, "Rute belum tersedia"),
      orders,
      salesOrderCount: orders.length || Number(stop?.salesOrderCount || 0),
      totalWeightKg: toFiniteNumber(stop?.totalWeightKg),
      totalVolumeM3: toFiniteNumber(stop?.totalVolumeM3),
    };
  });
}

export function getMonitoringSummary(fleetOrder) {
  const stops = normalizeMonitoringStops(fleetOrder);
  const uniqueDocuments = new Set();

  stops.forEach((stop) => {
    stop.orders.forEach((order) =>
      uniqueDocuments.add(`${order.documentType}:${order.documentNumber}`.toLowerCase()),
    );
  });

  return {
    stopCount: stops.length || Number(fleetOrder?.stopCount || 0),
    documentCount:
      uniqueDocuments.size ||
      Number(fleetOrder?.documentCount || fleetOrder?.salesOrderCount || fleetOrder?.orderCount || 0),
    salesOrderCount:
      uniqueDocuments.size || Number(fleetOrder?.salesOrderCount || fleetOrder?.orderCount || 0),
    customerCount: Number(fleetOrder?.customerCount || stops.length || 0),
    totalWeightKg: toFiniteNumber(fleetOrder?.totalWeightKg),
    totalVolumeM3: toFiniteNumber(fleetOrder?.totalVolumeM3),
  };
}

export function canShareFleetOrderMonitoring(fleetOrder) {
  const stops = normalizeMonitoringStops(fleetOrder);
  const summary = getMonitoringSummary(fleetOrder);

  if (summary.documentCount <= 0) {
    return {
      allowed: false,
      reason: "Fleet Order belum memiliki dokumen aktif.",
    };
  }

  if (stops.length <= 0) {
    return {
      allowed: false,
      reason: "Data stop Fleet Order belum tersedia.",
    };
  }

  return {
    allowed: true,
    reason: "",
  };
}

export function buildMonitoringPages(fleetOrder, options = {}) {
  const requestedMaxStops = Number(options.maxStopsPerPage);
  const maxStopsPerPage = Number.isFinite(requestedMaxStops)
    ? Math.max(1, Math.min(MONITORING_MAX_STOPS_PER_PAGE, requestedMaxStops))
    : MONITORING_MAX_STOPS_PER_PAGE;
  const stops = normalizeMonitoringStops(fleetOrder).map((stop) => ({
    ...stop,
    monitoringLayout: getMonitoringStopLayout(stop),
  }));
  const summary = getMonitoringSummary(fleetOrder);
  const status = formatMonitoringStatus(fleetOrder?.status);

  if (!stops.length) return [];

  const minimumPageCount = getMinimumMonitoringPageCount(
    stops,
    maxStopsPerPage,
  );
  const groupedStops = partitionMonitoringStops(
    stops,
    minimumPageCount,
    maxStopsPerPage,
  );

  return groupedStops.map((pageStops, pageIndex) => ({
    pageIndex,
    pageNumber: pageIndex + 1,
    pageCount: groupedStops.length,
    stops: pageStops,
    totalStops: stops.length,
    summary,
    status,
  }));
}

export function buildMonitoringFileName(fleetOrder, pageIndex = 0, pageCount = 1) {
  const base = `monitoring-${sanitizeFilename(fleetOrder?.foNumber || "fleet-order")}`;

  if (pageCount > 1) {
    return `${base}-${pageIndex + 1}-of-${pageCount}.png`;
  }

  return `${base}.png`;
}

export function buildWhatsAppCaption(fleetOrder) {
  const stops = normalizeMonitoringStops(fleetOrder);
  const summary = getMonitoringSummary(fleetOrder);
  const status = formatMonitoringStatus(fleetOrder?.status);
  const routeName = text(fleetOrder?.foRouteName || fleetOrder?.routeName, "Rute belum tersedia");
  const vehicle = [
    text(fleetOrder?.vehiclePlate, ""),
    text(fleetOrder?.vehicleType, ""),
  ]
    .filter(Boolean)
    .join(" · ");
  const lines = [
    `*${status.captionTitle}*`,
    "",
    `*FO:* ${text(fleetOrder?.foNumber)}`,
    `*Status:* ${status.label}`,
    `*Rute:* ${routeName}`,
    `*Berangkat:* ${formatDate(fleetOrder?.departureDate)}`,
    `*Estimasi Pulang:* ${formatDate(fleetOrder?.estimatedReturnDate)}`,
    `*Driver:* ${text(fleetOrder?.driverName)}`,
    `*Co-Driver:* ${text(fleetOrder?.coDriverName)}`,
    `*Armada:* ${vehicle || "Armada belum tersedia"}`,
    "",
    "*Daftar Pengiriman*",
    "",
  ];

  stops.forEach((stop, index) => {
    lines.push(`${index + 1}. *${stop.customerName}*`);

    if (stop.hasCustomAddressAlias) {
      lines.push(`   Alamat: ${stop.addressAlias}`);
    }

    lines.push(`   Area: ${stop.resolvedArea}`);
    lines.push(`   Rute: ${stop.resolvedRouteName}`);

    if (stop.orders.length) {
      stop.orders.forEach((order) => {
        lines.push(`   ${order.displayLabel || order.documentNumber}`);
      });
    } else {
      lines.push("   Dokumen belum tersedia");
    }

    lines.push("");
  });

  lines.push(`*Total:* ${summary.stopCount} stop · ${summary.documentCount} dokumen`);
  lines.push(
    `*Muatan:* ${formatMetric(summary.totalWeightKg, "kg")} · ${formatMetric(
      summary.totalVolumeM3,
      "m³",
    )}`,
  );

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function roundRect(ctx, x, y, width, height, radius) {
  const value = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + value, y);
  ctx.lineTo(x + width - value, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + value);
  ctx.lineTo(x + width, y + height - value);
  ctx.quadraticCurveTo(x + width, y + height, x + width - value, y + height);
  ctx.lineTo(x + value, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - value);
  ctx.lineTo(x, y + value);
  ctx.quadraticCurveTo(x, y, x + value, y);
  ctx.closePath();
}

function fillRoundRect(ctx, x, y, width, height, radius, fillStyle) {
  ctx.save();
  roundRect(ctx, x, y, width, height, radius);
  ctx.fillStyle = fillStyle;
  ctx.fill();
  ctx.restore();
}

function strokeRoundRect(ctx, x, y, width, height, radius, strokeStyle, lineWidth = 1) {
  ctx.save();
  roundRect(ctx, x, y, width, height, radius);
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
  ctx.restore();
}

function drawText(ctx, value, x, y, options = {}) {
  const {
    color = BRAND.ink,
    size = 24,
    weight = 600,
    align = "left",
    baseline = "alphabetic",
  } = options;

  ctx.save();
  ctx.fillStyle = color;
  ctx.font = `${weight} ${size}px Inter, Segoe UI, Arial, sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.fillText(value, x, y);
  ctx.restore();
}

function truncateText(ctx, value, maxWidth) {
  const source = text(value, "");

  if (ctx.measureText(source).width <= maxWidth) return source;

  let result = source;

  while (result.length > 1 && ctx.measureText(`${result}…`).width > maxWidth) {
    result = result.slice(0, -1);
  }

  return `${result}…`;
}

function drawWrappedText(ctx, value, x, y, maxWidth, options = {}) {
  const {
    color = BRAND.ink,
    size = 24,
    weight = 500,
    lineHeight = Math.round(size * 1.3),
    maxLines = 2,
  } = options;
  const words = text(value, "").split(/\s+/);
  const lines = [];
  let current = "";

  ctx.save();
  ctx.font = `${weight} ${size}px Inter, Segoe UI, Arial, sans-serif`;

  words.forEach((word) => {
    const attempt = current ? `${current} ${word}` : word;

    if (ctx.measureText(attempt).width <= maxWidth) {
      current = attempt;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  });

  if (current) lines.push(current);

  const visible = lines.slice(0, maxLines);

  if (lines.length > maxLines && visible.length) {
    visible[visible.length - 1] = truncateText(ctx, visible[visible.length - 1], maxWidth - 12);
  }

  ctx.fillStyle = color;
  ctx.font = `${weight} ${size}px Inter, Segoe UI, Arial, sans-serif`;
  visible.forEach((line, index) => {
    ctx.fillText(line, x, y + index * lineHeight);
  });
  ctx.restore();

  return y + visible.length * lineHeight;
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (typeof Image === "undefined") {
      resolve(null);
      return;
    }

    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("PNG monitoring tidak dapat dibuat."));
        return;
      }

      resolve(blob);
    }, "image/png");
  });
}

function drawLogo(ctx, logoImage) {
  const x = 60;
  const y = 50;
  const size = 72;

  if (logoImage) {
    ctx.drawImage(logoImage, x, y, size, size);
    return;
  }

  fillRoundRect(ctx, x, y, size, size, 20, BRAND.primary);
  drawText(ctx, "RP", x + size / 2, y + 45, {
    align: "center",
    color: "#FFFFFF",
    size: 24,
    weight: 800,
  });
}

function drawBadge(ctx, label, x, y, options = {}) {
  const {
    color = BRAND.primary,
    background = BRAND.soft,
    paddingX = 22,
    height = 38,
    size = 18,
  } = options;

  ctx.save();
  ctx.font = `800 ${size}px Inter, Segoe UI, Arial, sans-serif`;
  const width = ctx.measureText(label).width + paddingX * 2;

  fillRoundRect(ctx, x, y, width, height, height / 2, background);
  drawText(ctx, label, x + width / 2, y + height / 2 + 6, {
    align: "center",
    color,
    size,
    weight: 800,
  });
  ctx.restore();

  return width;
}

function drawInfoColumn(ctx, items, x, y, width) {
  let currentY = y;

  items.forEach((item) => {
    drawText(ctx, item.label.toUpperCase(), x, currentY, {
      color: BRAND.muted,
      size: 17,
      weight: 800,
    });
    drawWrappedText(ctx, item.value, x, currentY + 30, width, {
      color: BRAND.ink,
      size: 25,
      weight: 760,
      lineHeight: 30,
      maxLines: 1,
    });

    currentY += 76;
  });
}

function drawMetricCard(ctx, label, value, x, y, width) {
  fillRoundRect(ctx, x, y, width, 88, 18, "#FFFFFF");
  strokeRoundRect(ctx, x, y, width, 88, 18, BRAND.line, 1);
  drawText(ctx, label.toUpperCase(), x + 22, y + 30, {
    color: BRAND.muted,
    size: 16,
    weight: 800,
  });
  drawWrappedText(ctx, value, x + 22, y + 64, width - 44, {
    color: BRAND.ink,
    size: 26,
    weight: 850,
    maxLines: 1,
  });
}

function drawStopCard(
  ctx,
  stop,
  index,
  x,
  y,
  width,
  height,
  routeLineX,
  isLast,
) {
  const markerY = y + height / 2;
  const markerRadius = 25;

  ctx.save();
  ctx.strokeStyle = BRAND.primary;
  ctx.lineWidth = 5;

  if (index > 0) {
    ctx.beginPath();
    ctx.moveTo(routeLineX, y - MONITORING_STOP_GAP);
    ctx.lineTo(routeLineX, markerY - markerRadius);
    ctx.stroke();
  }

  if (!isLast) {
    ctx.beginPath();
    ctx.moveTo(routeLineX, markerY + markerRadius);
    ctx.lineTo(routeLineX, y + height + MONITORING_STOP_GAP);
    ctx.stroke();
  }

  ctx.restore();

  fillRoundRect(
    ctx,
    routeLineX - markerRadius,
    markerY - markerRadius,
    markerRadius * 2,
    markerRadius * 2,
    markerRadius,
    BRAND.primary,
  );
  drawText(
    ctx,
    String(stop.displaySequence).padStart(2, "0"),
    routeLineX,
    markerY + 8,
    {
      align: "center",
      color: "#FFFFFF",
      size: 20,
      weight: 850,
    },
  );

  fillRoundRect(ctx, x, y, width, height, 18, "#FFFFFF");
  strokeRoundRect(ctx, x, y, width, height, 18, BRAND.line, 1);

  const layout = stop.monitoringLayout || getMonitoringStopLayout(stop);
  const titleLines = layout.titleLines;
  const orderLines = layout.orderLines;
  const contentX = x + 20;
  const contentWidth = width - 40;
  const titleStartY = y + 28;

  titleLines.forEach((line, lineIndex) => {
    ctx.save();
    ctx.font = "850 24px Inter, Segoe UI, Arial, sans-serif";
    drawText(
      ctx,
      truncateText(ctx, line, contentWidth),
      contentX,
      titleStartY + lineIndex * 25,
      {
        color: BRAND.ink,
        size: 24,
        weight: 850,
      },
    );
    ctx.restore();
  });

  const metadataY = titleStartY + Math.max(0, titleLines.length - 1) * 25 + 27;
  const destinationLine = `${stop.addressAlias} · ${stop.resolvedArea} · ${stop.resolvedRouteName}`;

  ctx.save();
  ctx.font = "600 18px Inter, Segoe UI, Arial, sans-serif";
  drawText(
    ctx,
    truncateText(ctx, destinationLine, contentWidth),
    contentX,
    metadataY,
    {
      color: BRAND.muted,
      size: 18,
      weight: 600,
    },
  );
  ctx.restore();

  const ordersStartY = metadataY + 26;

  orderLines.forEach((line, lineIndex) => {
    /*
     * Character-budget wrapping is deliberately conservative so a complete
     * Document number stays on one line. We still guard against unusual fonts by
     * using the full card width rather than clipping to a fixed row height.
     */
    drawText(
      ctx,
      line,
      contentX,
      ordersStartY + lineIndex * 20,
      {
        color: BRAND.primary,
        size: 18,
        weight: 800,
      },
    );
  });
}

export async function generateMonitoringPagePng(page, fleetOrder, options = {}) {
  if (typeof document === "undefined") {
    throw new Error("Browser tidak mendukung pembuatan PNG monitoring.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = MONITORING_CARD_WIDTH;
  canvas.height = MONITORING_CARD_HEIGHT;

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas browser tidak tersedia.");
  }

  const logoImage = await loadImage(
    options.logoSrc || "/brand/route-planning-mark-transparent-512.png",
  );
  const status = page.status || formatMonitoringStatus(fleetOrder?.status);
  const routeName = text(fleetOrder?.foRouteName || fleetOrder?.routeName, "Rute belum tersedia");
  const vehicle = [
    text(fleetOrder?.vehiclePlate, ""),
    text(fleetOrder?.vehicleType, ""),
  ]
    .filter(Boolean)
    .join(" · ");
  const summary = page.summary || getMonitoringSummary(fleetOrder);
  const generatedAt = options.generatedAt || new Date();

  ctx.fillStyle = BRAND.canvas;
  ctx.fillRect(0, 0, MONITORING_CARD_WIDTH, MONITORING_CARD_HEIGHT);

  fillRoundRect(ctx, 32, 32, MONITORING_CARD_WIDTH - 64, MONITORING_CARD_HEIGHT - 64, 32, "#FFFFFF");
  strokeRoundRect(ctx, 32, 32, MONITORING_CARD_WIDTH - 64, MONITORING_CARD_HEIGHT - 64, 32, BRAND.line, 1);

  drawLogo(ctx, logoImage);
  drawText(ctx, "MONITORING PENGIRIMAN", 148, 75, {
    color: BRAND.primary,
    size: 34,
    weight: 900,
  });
  drawText(ctx, text(fleetOrder?.foNumber), 148, 115, {
    color: BRAND.ink,
    size: 30,
    weight: 850,
  });

  drawBadge(ctx, status.badge, 780, 58, {
    color: status.color,
    background: status.background,
  });

  if (page.pageCount > 1) {
    drawText(ctx, `Bagian ${page.pageNumber} dari ${page.pageCount}`, 1010, 126, {
      align: "right",
      color: BRAND.muted,
      size: 18,
      weight: 800,
    });
  }

  fillRoundRect(ctx, 60, 150, 960, 180, 24, BRAND.soft);
  drawInfoColumn(
    ctx,
    [
      {
        label: "Rute FO",
        value: routeName,
      },
      {
        label: "Berangkat",
        value: formatDate(fleetOrder?.departureDate),
      },
    ],
    88,
    188,
    385,
  );
  drawInfoColumn(
    ctx,
    [
      {
        label: "Estimasi Pulang",
        value: formatDate(fleetOrder?.estimatedReturnDate),
      },
      {
        label: "Driver",
        value: text(fleetOrder?.driverName),
      },
    ],
    500,
    188,
    270,
  );
  drawInfoColumn(
    ctx,
    [
      {
        label: "Co-Driver",
        value: text(fleetOrder?.coDriverName),
      },
      {
        label: "Armada",
        value: vehicle || "Armada belum tersedia",
      },
    ],
    780,
    188,
    210,
  );

  const metricY = 350;
  const metricWidth = 225;
  const metricGap = 20;
  drawMetricCard(ctx, "Stop", `${summary.stopCount} stop`, 60, metricY, metricWidth);
  drawMetricCard(ctx, "Dokumen", `${summary.documentCount} dokumen`, 60 + (metricWidth + metricGap), metricY, metricWidth);
  drawMetricCard(ctx, "Berat", formatMetric(summary.totalWeightKg, "kg"), 60 + (metricWidth + metricGap) * 2, metricY, metricWidth);
  drawMetricCard(ctx, "Volume", formatMetric(summary.totalVolumeM3, "m³"), 60 + (metricWidth + metricGap) * 3, metricY, metricWidth);

  drawText(ctx, "URUTAN PENGIRIMAN", 60, 476, {
    color: BRAND.primary,
    size: 22,
    weight: 900,
  });

  drawText(ctx, `${page.stops.length} stop pada halaman ini`, 1020, 476, {
    align: "right",
    color: BRAND.muted,
    size: 18,
    weight: 700,
  });

  const stopStartY = 500;
  const routeLineX = 90;
  let currentStopY = stopStartY;

  page.stops.forEach((stop, index) => {
    const stopHeight = Number(
      stop.monitoringLayout?.height || getMonitoringStopLayout(stop).height,
    );

    drawStopCard(
      ctx,
      stop,
      index,
      128,
      currentStopY,
      892,
      stopHeight,
      routeLineX,
      index === page.stops.length - 1,
    );

    currentStopY += stopHeight + MONITORING_STOP_GAP;
  });

  ctx.save();
  ctx.strokeStyle = BRAND.line;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(60, 1256);
  ctx.lineTo(1020, 1256);
  ctx.stroke();
  ctx.restore();

  drawText(ctx, `${summary.stopCount} Stop · ${summary.documentCount} Dokumen`, 60, 1290, {
    color: BRAND.ink,
    size: 22,
    weight: 850,
  });
  drawText(
    ctx,
    `${formatMetric(summary.totalWeightKg, "kg")} · ${formatMetric(summary.totalVolumeM3, "m³")}`,
    60,
    1320,
    {
      color: BRAND.muted,
      size: 18,
      weight: 700,
    },
  );
  drawText(ctx, `Dibuat dari Route Planning · ${formatTimestamp(generatedAt)}`, 1020, 1308, {
    align: "right",
    color: BRAND.muted,
    size: 17,
    weight: 650,
  });

  return canvasToBlob(canvas);
}
